/**
 * Error Message Handler
 * Standardized error messages for the entire application
 */

export const ERROR_MESSAGES = {
  // Auth errors
  NOT_AUTHENTICATED: 'You are not authenticated. Please login.',
  TOKEN_NOT_FOUND: 'Authentication token not found. Please login again.',
  UNAUTHORIZED: 'You do not have permission to perform this action.',
  
  // File upload errors
  FILE_TOO_LARGE: 'File size is too large. Maximum size: 5MB.',
  INVALID_FILE_TYPE: 'Invalid file type. Please upload a valid file.',
  FILE_UPLOAD_FAILED: 'Failed to upload file. Please try again.',
  IMAGE_SIZE_TOO_LARGE: 'Image size is too large. Maximum size: 2MB.',
  INVALID_IMAGE_FORMAT: 'Invalid image format. Allowed formats: JPG, PNG, GIF.',
  
  // Network errors
  NETWORK_ERROR: 'Network connection error. Please check your internet connection.',
  SERVER_ERROR: 'Internal server error. Please try again later.',
  REQUEST_TIMEOUT: 'Request timeout. Please try again.',
  
  // Validation errors
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Invalid email address.',
  INVALID_PHONE: 'Invalid phone number.',
  INVALID_FORMAT: 'Invalid format.',
  DUPLICATE_ENTRY: 'This entry already exists.',
  
  // CRUD errors
  ITEM_NOT_FOUND: 'Item not found.',
  FAILED_TO_CREATE: 'Failed to create item. Please try again.',
  FAILED_TO_UPDATE: 'Failed to update item. Please try again.',
  FAILED_TO_DELETE: 'Failed to delete item. Please try again.',
  FAILED_TO_FETCH: 'Failed to load data. Please try again.',
  
  // Generic errors
  SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
  TRY_AGAIN_LATER: 'Please try again later.',
  INVALID_DATA: 'Invalid data format.',
};

/**
 * Handle API errors and return user-friendly messages
 * @param {Error|Object} error - The error object or response
 * @param {string} fallbackMessage - Fallback message if no match found
 * @returns {string} User-friendly error message
 */
export const handleError = (error, fallbackMessage = ERROR_MESSAGES.SOMETHING_WENT_WRONG) => {
  // Handle response object from API
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle error message field
  if (error?.message) {
    const msg = error.message.toLowerCase();

    // File size errors
    if (msg.includes('file') && msg.includes('size')) {
      return ERROR_MESSAGES.FILE_TOO_LARGE;
    }
    if (msg.includes('image') && msg.includes('size')) {
      return ERROR_MESSAGES.IMAGE_SIZE_TOO_LARGE;
    }

    // File type errors
    if (msg.includes('file type') || msg.includes('invalid format')) {
      return ERROR_MESSAGES.INVALID_FILE_TYPE;
    }
    if (msg.includes('image') && msg.includes('format')) {
      return ERROR_MESSAGES.INVALID_IMAGE_FORMAT;
    }

    // Network errors
    if (msg.includes('network') || msg.includes('fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (msg.includes('timeout')) {
      return ERROR_MESSAGES.REQUEST_TIMEOUT;
    }

    // Auth errors
    if (msg.includes('unauthorized') || msg.includes('403')) {
      return ERROR_MESSAGES.UNAUTHORIZED;
    }
    if (msg.includes('token') || msg.includes('authentication')) {
      return ERROR_MESSAGES.TOKEN_NOT_FOUND;
    }

    // Not found
    if (msg.includes('not found') || msg.includes('404')) {
      return ERROR_MESSAGES.ITEM_NOT_FOUND;
    }

    // Server errors
    if (msg.includes('server') || msg.includes('500')) {
      return ERROR_MESSAGES.SERVER_ERROR;
    }

    // Duplicate
    if (msg.includes('duplicate') || msg.includes('already exists')) {
      return ERROR_MESSAGES.DUPLICATE_ENTRY;
    }

    return error.message;
  }

  // Handle status codes
  if (error?.status) {
    switch (error.status) {
      case 400:
        return ERROR_MESSAGES.INVALID_DATA;
      case 401:
        return ERROR_MESSAGES.NOT_AUTHENTICATED;
      case 403:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 404:
        return ERROR_MESSAGES.ITEM_NOT_FOUND;
      case 408:
        return ERROR_MESSAGES.REQUEST_TIMEOUT;
      case 413:
        return ERROR_MESSAGES.FILE_TOO_LARGE;
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR;
      case 503:
        return ERROR_MESSAGES.TRY_AGAIN_LATER;
      default:
        return fallbackMessage;
    }
  }

  return fallbackMessage;
};

/**
 * Get error message from various error formats
 * Handles response.json(), Error objects, strings, etc.
 */
export const getErrorMessage = (error, context = '') => {
  try {
    // If it's already a string, check if it's a known error key
    if (typeof error === 'string') {
      // Check if it's an error key in ERROR_MESSAGES
      if (ERROR_MESSAGES[error]) {
        return ERROR_MESSAGES[error];
      }
      return error;
    }

    // Check for response data
    if (error?.data?.message) {
      return error.data.message;
    }

    // Check for response message
    if (error?.message && typeof error.message === 'string') {
      return handleError(error);
    }

    // Check for status code mapping
    if (error?.status) {
      return handleError(error);
    }

    return `${context ? context + ': ' : ''}${ERROR_MESSAGES.SOMETHING_WENT_WRONG}`;
  } catch (e) {
    return ERROR_MESSAGES.SOMETHING_WENT_WRONG;
  }
};
